import { makeStyles } from "@material-ui/core/styles";

import withAuthentication from "hocs/withAuthentication";
import withMainLayout from "hocs/withMainLayout";
import { Box, Grid, Paper, TextField, Typography, Button, Divider, CircularProgress } from "@material-ui/core";
import BaseScreen from "components/BaseScreen";
import HorizontalArragement from "components/HorizontalArragement";
import ChildrenFriendlyButton from "components/ChildrenFriendlyButton";
import FormikTextField from "components/FormikTextField";
import Thumbnail from "components/Thumbnail";
import { Autocomplete } from "@material-ui/lab";
import { useFormik } from "formik";
import * as yup from "yup";
import { useResource } from "hooks";
import APIEndpoints from "APIEndpoints";
import SelectImage from "components/SelectImage";
import useHit from "hooks/useHit";
import useAlerts from "hooks/useAlerts";
import { useRouter } from "next/router";
import routes from "routes";
import { useState } from "react";
import { useDialogs } from "store/Dialogs";
import RichTextCanvas from "components/RichTextCanvas";
import { useTranslation } from "i18n";
import { Node } from "slate";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
  },
  inputs: {
    "& > *": {
      display: "block",
      marginBottom: theme.spacing(2),
    },
  },
  submit: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  },
  thumbnailBox: {
    border: "1px solid #666",
    borderRadius: 4,
  },
}));

const validationSchema = yup.object({
  title: yup.string().min(1).max(40).required(),
  subtitle: yup.string().min(1).max(80).required(),
  thumbnail: yup.string().url().required(),
  topics: yup.array().min(1).max(3).required(),
});

const CreatePost = () => {
  const { data: topics } = useResource(
    APIEndpoints.GLOBALS.GET_TOPICS,
    { options: { revalidate: false } },
  );
  const [isThumbnailLoading, setThumbnailLoading] = useState(false);
  const hit = useHit();
  const [text, setText] = useState(null);
  const alerts = useAlerts();
  const classes = useStyles();
  const dialogs = useDialogs();
  const { t } = useTranslation();
  const router = useRouter();
  const formik = useFormik({
    initialValues: {
      title: "",
      subtitle: "",
      thumbnail: "",
      topics: [],
    },
    validationSchema,
    onSubmit: async ({
      title,
      subtitle,
      thumbnail,
      topics,
    }) => {
      const editorText = text?.map((n) => Node.string(n)).join(" ");
      if (!editorText || !editorText.length) return alert(t("TEXT_EMPTY"));
      if (editorText.length < 100) return alert([t("TEXT_TOO_SMALL"), editorText.length].join(""));
      const { error, ...post } = await hit(
        APIEndpoints.POSTS.CREATE,
        {
          title,
          subtitle,
          thumbnail,
          text: JSON.stringify(text),
          pretext: editorText.substring(0, 300),
          topics: topics.map((t) => t.id),
        },
      );
      if (error) return alerts.error(error);
      const dialog = dialogs.create({
        title: t("POST_PUBLISHED_SUCCESSFULLY_TITLE"),
        subtitle: t("POST_PUBLISHED_SUCCESSFULLY_SUBTITLE"),
        actions: [
          {
            text: t("CONTINUE"),
            onClick: () => {
              router.push(routes.POST(post.id));
              dialog.close();
            },
          },
        ],
      });
    },
  });
  return (
    <BaseScreen>
      <Grid container justifyContent="center">
        <Grid item xs={12} sm={8} md={8}>
          <Typography variant="h6" className="bold">
            {t("POST_DETAILS")}
          </Typography>
          <Paper className={classes.paper}>
            <Box className={classes.inputs}>
              <FormikTextField
                formik={formik}
                required
                name="title"
                fullWidth
              />
              <FormikTextField
                formik={formik}
                name="subtitle"
                required
                fullWidth
              />
              <Autocomplete
                getOptionLabel={(topic) => topic.id}
                ChipProps={{ size: "small", disabled: false }}
                disabled={formik.values.topics.length >= 3}
                filterSelectedOptions
                loading={!topics}
                options={topics || []}
                multiple
                onChange={(e, val) => formik.setFieldValue("topics", val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={formik.touched.topics && !!formik.errors.topics}
                    helperText={formik.touched.topics && formik.errors.topics}
                    label="Topics - (Up to 3)"
                    required
                    disabled={formik.values.topics.length >= 3}
                  />
                )}
              />

            </Box>
            <Typography
              color={formik.touched.thumbnail && !!formik.errors.thumbnail ? "error" : "textSecondary"}
            >
              Thumbnail *
            </Typography>
            <SelectImage
              onChange={(url) => formik.setFieldValue("thumbnail", url)}
              onLoading={setThumbnailLoading}
            >
              <ChildrenFriendlyButton
                className={classes.thumbnailBox}
                fullWidth
              >
                <HorizontalArragement justifyContent="space-between">
                  <HorizontalArragement>
                    <Thumbnail src={formik.values.thumbnail} />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                    >
                      {t("RECOMMENDED_IMAGE_SIZE")}
                      {" "}
                      - 180x180
                      <br />
                      JPEG, PNG, GIF. Max Size: 1MB
                    </Typography>
                  </HorizontalArragement>
                  {isThumbnailLoading && <CircularProgress size={20} />}
                </HorizontalArragement>
              </ChildrenFriendlyButton>
            </SelectImage>
          </Paper>
          <Divider variant="inset" />
          <Typography variant="h6" display="block" className="bold">
            Post
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            paragraph
          >
            {t("POST_WRITE_SUBTITLE")}
          </Typography>
          <Paper className={classes.paper}>
            <RichTextCanvas enableToolbar onChange={setText} />
          </Paper>
          <Box className={classes.submit}>
            <Button
              disabled={formik.isSubmitting}
              onClick={formik.handleSubmit}
              variant="contained"
              color="primary"
            >
              {t("PUBLISH")}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </BaseScreen>
  );
};

export default withMainLayout(withAuthentication(CreatePost));
